import axios from 'axios';
import { API_URL } from '../constants/api';
import {
  SchemaElement,
  NodeWithPosition,
  OperationTemplate,
  OperationTemplateRequest,
  NodePositionUpdate,
  MapData,
} from '../types/mapEditor';
import { universalAuthService } from './universalAuthService';

// Create authenticated axios instance with token refresh
const createAuthClient = (tokenKey: string, userType: 'field' | 'referee') => {
  const client = axios.create({ baseURL: API_URL });

  // Request interceptor - add token
  client.interceptors.request.use((config) => {
    const token = localStorage.getItem(tokenKey);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // Response interceptor - handle 401 and refresh token
  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // If 401 and haven't retried yet
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        console.log(`[${userType}] 401 error in mapEditorService, attempting token refresh...`);

        try {
          const { refreshAccessToken } = await import('./authService');
          const newTokens = await refreshAccessToken(userType);

          if (newTokens) {
            console.log(`[${userType}] Token refresh successful, retrying request...`);

            // Update tokens in localStorage
            universalAuthService.updateTokens(userType, newTokens.token, newTokens.refreshToken);

            // Update Authorization header
            originalRequest.headers.Authorization = `Bearer ${newTokens.token}`;

            // Retry original request
            return client(originalRequest);
          } else {
            console.log(`[${userType}] Token refresh failed`);
            universalAuthService.clearData(userType);
            window.location.href = '/';
          }
        } catch (refreshError) {
          console.error(`[${userType}] Token refresh error:`, refreshError);
          universalAuthService.clearData(userType);
          window.location.href = '/';
        }
      }

      return Promise.reject(error);
    }
  );

  return client;
};

// Field API client
const fieldApi = createAuthClient('fieldToken', 'field');

// Referee API client
const refereeApi = createAuthClient('refereeToken', 'referee');

// Determine which API to use based on user type
const getApiClient = () => {
  const fieldToken = localStorage.getItem('fieldToken');
  const refereeToken = localStorage.getItem('refereeToken');

  if (fieldToken) {
    return { client: fieldApi, baseUrl: '/api/field/map-editor' };
  } else if (refereeToken) {
    return { client: refereeApi, baseUrl: '/api/main-referee/map-editor' };
  }

  throw new Error('No authentication token found');
};

// Get map data with schema elements
export const getMapData = async (mapId: string): Promise<MapData> => {
  const { client, baseUrl } = getApiClient();

  // Get map from the appropriate endpoint
  const fieldToken = localStorage.getItem('fieldToken');
  let url = fieldToken ? '/api/field/maps/my' : '/api/main-referee/maps';

  const response = await client.get(url);
  const maps = response.data as MapData[];
  const map = maps.find((m: MapData) => m.id === mapId);

  if (!map) {
    throw new Error('Map not found');
  }

  return map;
};

// Get nodes for a map
export const getMapNodes = async (mapId: string): Promise<NodeWithPosition[]> => {
  const { client, baseUrl } = getApiClient();
  const response = await client.get(`${baseUrl}/maps/${mapId}/nodes`);
  return response.data;
};

// Update schema elements
export const updateSchemaElements = async (
  mapId: string,
  elements: SchemaElement[],
  canvasBgColor?: 'dark' | 'white'
): Promise<MapData> => {
  const { client, baseUrl } = getApiClient();
  const response = await client.put(`${baseUrl}/maps/${mapId}/schema-elements`, {
    elements,
    canvasBgColor,
  });
  return response.data;
};

// Update single node position
export const updateNodePosition = async (
  mapId: string,
  nodeId: string,
  positionX: number,
  positionY: number,
  scale: number,
  locked: boolean
): Promise<NodeWithPosition> => {
  const { client, baseUrl } = getApiClient();
  const response = await client.put(`${baseUrl}/maps/${mapId}/nodes/${nodeId}/position`, {
    positionX,
    positionY,
    scale,
    locked,
  });
  return response.data;
};

// Batch update node positions
export const batchUpdateNodePositions = async (
  mapId: string,
  positions: NodePositionUpdate[]
): Promise<void> => {
  const { client, baseUrl } = getApiClient();
  await client.put(`${baseUrl}/maps/${mapId}/nodes/positions`, {
    positions,
  });
};

// Upload exported image
export const uploadExportedImage = async (
  mapId: string,
  imageBlob: Blob
): Promise<string> => {
  const { client, baseUrl } = getApiClient();

  const formData = new FormData();
  formData.append('imageFile', imageBlob, `map-export-${mapId}.png`);

  const response = await client.post(`${baseUrl}/maps/${mapId}/export-image`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data.imageUrl;
};

// Get operation templates for a map
export const getOperationTemplates = async (mapId: string): Promise<OperationTemplate[]> => {
  const { client, baseUrl } = getApiClient();
  const response = await client.get(`${baseUrl}/maps/${mapId}/templates`);
  return response.data;
};

// Create operation template
export const createOperationTemplate = async (
  mapId: string,
  template: OperationTemplateRequest
): Promise<OperationTemplate> => {
  const { client, baseUrl } = getApiClient();
  const response = await client.post(`${baseUrl}/maps/${mapId}/templates`, template);
  return response.data;
};

// Update operation template
export const updateOperationTemplate = async (
  templateId: string,
  template: OperationTemplateRequest
): Promise<OperationTemplate> => {
  const { client, baseUrl } = getApiClient();
  const response = await client.put(`${baseUrl}/templates/${templateId}`, template);
  return response.data;
};

// Delete operation template
export const deleteOperationTemplate = async (templateId: string): Promise<void> => {
  const { client, baseUrl } = getApiClient();
  await client.delete(`${baseUrl}/templates/${templateId}`);
};

// Get active operations for field or referee
export const getActiveOperationsForMapEditor = async (): Promise<any[]> => {
  const fieldToken = localStorage.getItem('fieldToken');
  const refereeToken = localStorage.getItem('refereeToken');

  if (fieldToken) {
    // Field uses existing endpoint
    const response = await fieldApi.get('/api/field/operations/active');
    return response.data;
  } else if (refereeToken) {
    // Referee uses map-editor endpoint
    const response = await refereeApi.get('/api/main-referee/map-editor/operations/active');
    return response.data;
  }

  throw new Error('No authentication token found');
};

// Convert dataURL to Blob
export const dataURLtoBlob = (dataURL: string): Blob => {
  const arr = dataURL.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new Blob([u8arr], { type: mime });
};

export default {
  getMapData,
  getMapNodes,
  updateSchemaElements,
  updateNodePosition,
  batchUpdateNodePositions,
  uploadExportedImage,
  getOperationTemplates,
  createOperationTemplate,
  updateOperationTemplate,
  deleteOperationTemplate,
  dataURLtoBlob,
};
